import { useEffect } from "react";
import { ArrowLeft, ShieldCheck, Database, Mail, EyeOff, Cookie, Scale } from "lucide-react";
import { logo, CONTACT } from "@/data/content";

const SECTIONS = [
  {
    icon: Database,
    title: "Information we collect",
    body: [
      "When you submit an enquiry through our website, we collect the details you choose to share with us: your name, email address, phone number, company, the service you are interested in (investment, loans or partnership), the sector, your financing amount and the content of your message.",
      "We collect this information only when you voluntarily submit it. We do not collect any payment details, identity documents or financial statements through this website.",
    ],
  },
  {
    icon: EyeOff,
    title: "How your information is used",
    body: [
      "Your enquiry is used for one purpose only: to respond to your request. When you submit the form, your details are delivered to our team and an immediate notification is sent to our company inbox so we can reply, typically within one business day.",
      "We use your information to contact you about your enquiry, to evaluate and process any funding or partnership request you make, and to send internal summaries of enquiries to our own team. We never use your details for marketing lists, newsletters or advertising you did not ask for.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "How your information is stored and protected",
    body: [
      "Enquiries are stored in our secured database. Access is restricted to authorized Pacific Investment Group staff through a password-protected administrative dashboard, and all administrative sessions are encrypted.",
      "We apply strict internal access controls: only our team can view enquiry records, and records are never exposed publicly. Email alerts containing enquiry details are sent only to our official company address.",
    ],
  },
  {
    icon: Scale,
    title: "What we never do",
    body: [
      "We never sell, rent, trade or share your personal information with advertisers, data brokers or any third party for their own purposes.",
      "We never ask you to send passwords, card numbers, or banking credentials by email or through this website. If you ever receive a message claiming to be from us and asking for such details, do not respond — contact us directly at " + CONTACT.email + ".",
    ],
  },
  {
    icon: Cookie,
    title: "Cookies and local storage",
    body: [
      "This website stores one small preference in your browser: your chosen site language, so the site opens in your language next time. No advertising, tracking or analytics cookies are used.",
      "If you are an administrator, sign-in sessions use secure encrypted cookies that exist only for the duration of your dashboard access.",
    ],
  },
  {
    icon: Mail,
    title: "Your rights and data retention",
    body: [
      "You keep full control of your information. You may ask us at any time to show you the details we hold about you, to correct them, or to delete them entirely. We keep enquiry records only as long as they remain relevant to your request and our business relationship.",
      "To exercise any of these rights, simply email us at " + CONTACT.email + " and we will action your request promptly.",
    ],
  },
];

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="grain relative min-h-screen bg-white text-[#0F1D33]">
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
          <a href="/" data-testid="privacy-logo-link" className="flex items-center gap-3">
            <img src={logo} alt="Pacific Investment Group" className="h-11 w-11 rounded-lg object-cover ring-1 ring-gold/40" />
            <span className="leading-tight hidden sm:block">
              <span className="block font-display text-lg font-semibold tracking-wide">PACIFIC</span>
              <span className="block text-[10px] font-mono-brand tracking-[0.32em] text-gold-deep uppercase">Investment Group</span>
            </span>
          </a>
          <a
            href="/"
            data-testid="privacy-back-link"
            className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium"
          >
            <ArrowLeft size={15} strokeWidth={2} />
            Back to site
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <div className="flex items-center gap-3 font-mono-brand text-xs tracking-[0.28em] uppercase text-gold-deep mb-5">
          <span className="h-px w-10 bg-gold/70" />
          Legal
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">Privacy Policy</h1>
        <p className="mt-5 text-slate-600 leading-relaxed">
          At Pacific Investment Group, the confidentiality of your information is not an afterthought — it is a commitment. This policy explains, in plain words, exactly what we collect through this website, how it is used, and the protections around it.
        </p>
        <p className="mt-3 text-xs font-mono-brand tracking-wide text-slate-500">Last updated: October 18, 2026 · Applies to this website and all enquiry submissions made through it</p>

        <div className="mt-14 space-y-10">
          {SECTIONS.map((s, i) => (
            <section key={i} data-testid={`privacy-section-${i}`} className="card-navy card-navy-hover rounded-3xl p-7 sm:p-9">
              <div className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 text-gold-deep border border-gold/25 shrink-0">
                  <s.icon size={19} strokeWidth={1.75} />
                </span>
                <h2 className="font-display text-2xl sm:text-[1.7rem]">{s.title}</h2>
              </div>
              <div className="mt-5 space-y-4">
                {s.body.map((p, j) => (
                  <p key={j} className="text-sm sm:text-[15px] leading-relaxed text-slate-600">{p}</p>
                ))}
              </div>
            </section>
          ))}

          <section data-testid="privacy-section-contact" className="card-navy rounded-3xl p-7 sm:p-9">
            <h2 className="font-display text-2xl sm:text-[1.7rem]">Contact us</h2>
            <p className="mt-4 text-sm sm:text-[15px] leading-relaxed text-slate-600">
              Questions about this policy or the way your information is handled? We are happy to walk you through it.
            </p>
            <div className="mt-5 space-y-2 text-sm text-slate-700">
              <p>Email: <a href={`mailto:${CONTACT.email}`} className="text-gold-deep hover:underline">{CONTACT.email}</a></p>
              <p>WhatsApp: {CONTACT.whatsappNumber}</p>
              <p className="text-slate-500">{CONTACT.address}</p>
            </div>
          </section>

          <p className="text-xs leading-relaxed text-slate-500">
            We may update this policy from time to time to reflect improvements in our services. Any changes will be published on this page with a revised date. This policy was written in English; translated versions of the website are provided for convenience, and the English version applies.
          </p>
        </div>
      </main>

      <footer className="border-t border-gold/20 bg-[#F7F8FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">© 2026 Pacific Investment Group. All rights reserved.</p>
          <a href="/" data-testid="privacy-footer-home-link" className="text-xs text-gold-deep hover:underline">pacificinvestment.org</a>
        </div>
      </footer>
    </div>
  );
}
